// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define tooltips. The audience for this tool is someone who needs to
 * send a scan, not someone who wants a computer-vision lesson — so any term the
 * UI uses has to be one tap from a plain-English definition.
 */

export const GLOSSARY: Record<string, string> = {
  'on-device':
    'The work happens inside this browser tab, using your own processor. No copy of your photo is sent anywhere — you can disconnect from the internet and the tool still works.',
  perspective:
    'Photograph a rectangular page from anything other than straight above and it comes out as a trapezoid — the far edge looks shorter than the near one. Flattening the perspective undoes that and gives you the rectangle back.',
  homography:
    'The piece of maths that describes how a flat surface photographed from one angle maps onto the same surface seen from another. Knowing where four corners went is enough to work it out exactly, and once you have it you can undo the distortion for every pixel.',
  gradient:
    'How sharply brightness changes from one pixel to the next. It is near zero across a blank sheet of paper and large at the edge of the page, which is what makes it useful for finding where the paper stops.',
  'convex hull':
    'The smallest shape with no dents that contains a set of points — like stretching a rubber band around a scatter of pins. Because it only keeps the outermost points, the text in the middle of your page is ignored automatically.',
  'adaptive threshold':
    'Deciding whether each pixel is ink or paper by comparing it with the pixels immediately around it, rather than against one brightness cut-off for the whole image. It is what lets a page that is half in shadow still come out clean.',
  'integral image':
    'A precomputed table where every entry holds the sum of everything above and to the left of it. It lets you take the average of any rectangle, however big, with four lookups — which is why the shadow removal and the threshold do not slow down as their windows grow.',
  'shadow removal':
    'Your own head and hands usually cast a gradient across a page you are photographing. Papershot estimates that lighting by blurring the image until only the broad brightness variation is left, then divides it back out — the shadow goes, the text stays.',
  exif:
    'Hidden data cameras attach to photos — GPS coordinates, date, device serial number. Papershot rebuilds each page from pixels alone, so none of it survives into the PDF.',
};

let tooltip: HTMLElement | null = null;

function ensureTooltip(): HTMLElement {
  if (tooltip) return tooltip;
  tooltip = document.createElement('div');
  tooltip.className = 'glossary-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.hidden = true;
  document.body.append(tooltip);
  return tooltip;
}

function hide(): void {
  if (tooltip) tooltip.hidden = true;
}

function show(target: HTMLElement, term: string): void {
  const definition = GLOSSARY[term];
  if (!definition) return;

  const el = ensureTooltip();
  el.textContent = definition;
  el.hidden = false;

  const rect = target.getBoundingClientRect();
  const width = Math.min(320, window.innerWidth - 32);
  el.style.width = `${width}px`;

  const left = Math.min(Math.max(rect.left, 16), window.innerWidth - width - 16);
  el.style.left = `${left}px`;

  // Flip above the term if there isn't room below it.
  const below = rect.bottom + 8;
  if (below + el.offsetHeight > window.innerHeight - 16) {
    el.style.top = `${Math.max(16, rect.top - el.offsetHeight - 8)}px`;
  } else {
    el.style.top = `${below}px`;
  }
}

/** Wires every `.glossary-link[data-term]` in the document. */
export function initGlossary(): void {
  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!target) {
      hide();
      return;
    }
    event.preventDefault();
    const term = target.dataset.term;
    if (!term) return;

    if (!tooltip?.hidden && tooltip?.dataset.term === term) {
      hide();
      return;
    }
    show(target, term);
    if (tooltip) tooltip.dataset.term = term;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
  });
  window.addEventListener('resize', hide);
  window.addEventListener('scroll', hide, true);
}
